import { pick, omitBy, isArray, trim } from "lodash";

// quoted value to initiate exact matching
export const toExactMatchQueryString = val => (val ? (isArray(val) ? val.map(v => `"${v}"`) : `"${val}"`) : undefined);
// remove quotes from value to ensure proper UI rendering
export const fromExactMatchQueryString = val => (val ? (isArray(val) ? val.map(v => trim(v, '"')) : trim(val, '"')) : undefined);

export const keepOnlyPTABQueryStrings = query => {
  return omitBy(
    pick(query, [
      "case_number",
      "up_status",
      "up_substatus",
      "up_institution_phase",
      "patent.patent_number",
      "ptab_tech_center",
      "up_industry",
      "up_po_type",
      "up_pet_type",
      "filing_date",
      "up_institution_date",
      "up_termination_date",
      "petitioners",
      "lawfirms.petitioner",
      "judges.author",
      "patent_owners",
      "lawfirms.patent_owner",
      "judges.panel",
      "filings.full_text",
      "filings.up_type",
      "type",
      "claim_outcome_at_institution",
      "claim_outcome_at_fwd",
      "is_joined_case",
      "discretionary_denial_statutes",
      "discretionary_denial_refs",
      "errors",
      "sort"
    ]),
    v => v === "" || v === null || v === undefined
  );
};

export const keepOnlyLitigationQueryStrings = query => {
  return omitBy(
    pick(query, [
      "status",
      "case_no",
      "plaintiff",
      "defendant",
      "entity_type",
      "industry",
      "patents",
      "judge",
      "filed_date",
      "closed_date",
      "flag",
      "court",
      "cause_of_action",
      "sort",
      "sme_annotation",
      "npe_aggregator",
      "has_tplf",
      "product"
    ]),
    v => v === "" || v === null || v === undefined
  );
};

export const keepOnlyPatentSearchQueryStrings = query => {
  return omitBy(
    pick(query, [
      "ucid_spif",
      "assignee_parent",
      "assignee_current",
      "assignee_original",
      "inventors",
      "priority_date",
      "publication_date",
      "application_date",
      "expiration_date",
      "rating_broadness",
      "rating_citation",
      "rating_litigation",
      "portfolio_score",
      "family_id",
      "extended_family_id",
      "cpc_codes",
      "uspc_codes",
      "country",
      "doc_category",
      "transaction_type",
      "publication_type",
      "rnix_score",
      "publication_status",
      "opposition_country",
      "full_text",
      "full_text_fields",
      "is_litigated",
      "is_challenged"
    ]),
    v => v === "" || v === null || v === undefined
  );
};

export const keepOnlyJCTVCQueryStrings = query => {
  return omitBy(
    pick(query, ["standard", "full_text", "documents", "doc_id", "meeting", "authors", "status", "purpose", "source", "upload_date", "sort"]),
    v => v === "" || v === null || v === undefined
  );
};

export const keepOnlyRepo3GPPQueryStrings = query => {
  return omitBy(
    pick(query, [
      "full_text",
      "tdoc",
      "source",
      "type",
      "for",
      "meeting_date",
      "tdoc_status",
      "cr_category",
      "tsg_cr_pack",
      "sort",
      "meeting",
      "contact",
      "spec_standard",
      "is_user_equipment",
      "spec_group",
      "spec_initial_planned_release",
      "spec_series_no",
      "spec_type",
      "spec_spec_no"
    ]),
    v => v === "" || v === null || v === undefined
  );
};

export const keepOnlyRepoIeeeQueryStrings = query => {
  return omitBy(pick(query, ["full_text", "source", "author", "dcn", "rev", "year", "upload_date", "wgroup", "standard"]), v => v === "" || v === null || v === undefined);
};

export const keepOnlyRepoIetfQueryStrings = query => {
  return omitBy(pick(query, ["full_text", "source", "authors", "upload_date"]), v => v === "" || v === null || v === undefined);
};

export const keepOnlyProsecutionQueryStrings = query => {
  return omitBy(pick(query, ["art_unit"]), v => v === "" || v === null || v === undefined);
};

export const keepOnlyLandscapeQueryStrings = query => {
  return omitBy(
    pick(query, [
      "alpha_threshold",
      "techs",
      "pub_number",
      "pub_status",
      "assignee_current",
      "assignee_original",
      "assignee_parent",
      "inventors",
      "country",
      "country_pop",
      "family_id",
      "extended_family_id",
      "cpc_codes",
      "ipc_codes",
      "uspc_codes",
      "application_date",
      "priority_date",
      "publication_date",
      "expiration_date",
      "pools",
      "count_by",
      "declared_as_sep",
      "not",
      "full_text",
      "full_text_fields",
      "3gpp.spec",
      "3gpp.spec_series",
      "3gpp.2g_declaration_date",
      "3gpp.3g_declaration_date",
      "3gpp.4g_declaration_date",
      "3gpp.5g_declaration_date",
    ]),
    v => v === "" || v === null || v === undefined
  );
};

export const mapLandscapeQueryStringToName = qs => {
  return {
    count_by: "COUNT BY",
    techs: "TECHNOLOGIES",
    alpha_threshold: "SIMILARITY SCORE",
    pub_status: "PUBLICATION STATUS",
    pub_number: "PUB NUMBER",
    assignee_current: "CURRENT ASSIGNEE",
    assignee_original: "ORIGINAL ASSIGNEE",
    assignee_parent: "PARENT COMPANY",
    inventors: "INVENTORS",
    country: "PUBLICATION COUNTRY",
    country_pop: "PUBLICATION COUNTRY (LANDSCAPE)",
    family_id: "FAMILY ID",
    extended_family_id: "EXTENDED FAMILY ID",
    cpc_codes: "CPC CODES",
    ipc_codes: "IPC CODES",
    uspc_codes: "USPC CODES",
    application_date: "FILING DATE",
    priority_date: "PRIORITY DATE",
    publication_date: "PUBLICATION DATE",
    expiration_date: "EXPIRATION DATE",
    pools: "COLLECTIONS",
    declared_as_sep: "DECLARED AS SEP",
    not: "INVERTED FIELDS",
    full_text: "FULL TEXT",
    full_text_fields: "FULL TEXT FIELDS",
    "3gpp.spec": "SPECIFICATION",
    "3gpp.spec_series": "SPECIFICATION SERIES",
    "3gpp.2g_declaration_date": "2G DECLARATION DATE",
    "3gpp.3g_declaration_date": "3G DECLARATION DATE",
    "3gpp.4g_declaration_date": "4G DECLARATION DATE",
    "3gpp.5g_declaration_date": "5G DECLARATION DATE",
  }[qs];
};

export const keepOnlyInternationalQueryStrings = query => {
  return omitBy(
    pick(query, [
      "case_number",
      "patent_number",
      "filing_date",
      "patent_owner",
      "petitioner",
      "status",
      "decision_date",
      "jurisdiction"
    ]),
    v => v === "" || v === null || v === undefined
  );
};

export const keepOnlyUPCQueryStrings = query => {
  return omitBy(
    pick(query, [
      "case_number",
      "patent_number",
      "filing_date",
      "proceeding_type",
      "action_application_type",
      "respondent",
      "claimant",
      "representatives",
      "judges",
      "documents.full_text"
    ]),
    v => v === "" || v === null || v === undefined
  );
};

export const mapPTABQueryStringToName = qs => {
  return {
    case_number: "CASE NUMBER",
    up_status: "CASE STATUS",
    up_substatus: "CASE SUBSTATUS",
    up_institution_phase: "CASE PHASE",
    "patent.patent_number": "PATENT NUMBER",
    ptab_tech_center: "TECH CENTER",
    up_industry: "INDUSTRY",
    up_po_type: "P.O. ENTITY",
    up_pet_type: "PET. ENTITY",
    filing_date: "FILING DATE",
    up_institution_date: "INSTITUTION DATE",
    up_termination_date: "TERMINATION DATE",
    petitioners: "PETITIONER",
    "lawfirms.petitioner": "PET. COUNSEL",
    "judges.author": "AUTHOR JUDGE",
    patent_owners: "PATENT OWNER",
    "lawfirms.patent_owner": "P.O. COUNSEL",
    "judges.panel": "PANEL JUDGE",
    "filings.full_text": "FULL TEXT",
    "filings.up_type": "HAS FILING TYPE",
    type: "CASE TYPE",
    claim_outcome_at_institution: "INSTITUTION OUTCOMES",
    claim_outcome_at_fwd: "FWD OUTCOMES",
    is_joined_case: "JOINED",
    discretionary_denial_statutes: "DISCRETIONARY DENIAL STATUTES",
    discretionary_denial_refs: "§ 314(a) GROUNDS",
    errors: "POTENTIAL ERRORS",
    sort: "SORT"
  }[qs];
};

export const mapLitigationQueryStringToName = qs => {
  return {
    status: "Status",
    case_no: "Case Number",
    plaintiff: "Plaintiff",
    defendant: "Defendant",
    entity_type: "Entity Type",
    industry: "Industry",
    patents: "Patents in Case",
    judge: "Judge",
    filed_date: "Filing Date",
    closed_date: "Termination Date",
    flag: "Source",
    court: "Court",
    cause_of_action: "Cause of Action",
    sort: "Sort",
    sme_annotation: "Defendant",
    npe_aggregator: "NPE Aggregator",
    has_tplf: "Third Party Financing",
    product: "Infringed Product"
  }[qs];
};

export const mapJCTVCQueryStringToName = qs => {
  return {
    full_text: "FULL TEXT",
    documents: "DOCUMENTS",
    doc_id: "DOC ID",
    meeting: "MEETING",
    authors: "AUTHORS",
    status: "STATUS",
    purpose: "PURPOSE",
    source: "SOURCE",
    upload_date: "UPLOAD DATE",
    sort: "SORT",
    standard: "STANDARD"
  }[qs];
};

export const mapRepo3GPPQueryStringToName = qs => {
  return {
    full_text: "FULL TEXT",
    tdoc: "TDOC",
    source: "SOURCE",
    type: "TYPE",
    for: "FOR",
    meeting_date: "MEETING DATE",
    tdoc_status: "TDOC STATUS",
    cr_category: "CR CATEGORY",
    tsg_cr_pack: "TSG CR PACK",
    sort: "SORT",
    meeting: "MEETING",
    contact: "CONTACT",
    spec_standard: "STANDARDS",
    is_user_equipment: "TECHNICAL CATEGORY",
    spec_group: "SPECIFICATION GROUP",
    spec_initial_planned_release: "INITIAL PLANNED RELEASE",
    spec_series_no: "SPECIFICATION SERIES",
    spec_type: "SPECIFICATION TYPE",
    spec_spec_no: "SPECIFICATION NUMBER"
  }[qs];
};

export const mapRepoIeeeQueryStringToName = qs => {
  return {
    full_text: "FULL TEXT",
    source: "SOURCE",
    author: "AUTHOR",
    dcn: "DCN",
    rev: "REV",
    year: "YEAR",
    upload_date: "UPLOAD DATE",
    wgroup: "GROUP",
    standard: "WORKING GROUP"
  }[qs];
};

export const mapRepoIetfQueryStringToName = qs => {
  return {
    full_text: "FULL TEXT",
    source: "SOURCE",
    authors: "AUTHOR",
    upload_date: "UPLOAD DATE"
  }[qs];
};

export const mapPatentSearchQueryStringToName = qs => {
  return {
    ucid_spif: "PUB. NUM/SPIF",
    assignee_parent: "PARENT ASSIGNEE",
    assignee_current: "CURRENT ASSIGNEE",
    assignee_original: "ORIGINAL ASSIGNEE",
    inventors: "INVENTORS",
    priority_date: "PRIORITY DATE",
    publication_date: "PUBLICATION DATE",
    application_date: "APPLICATION DATE",
    expiration_date: "EXPIRATION DATE",
    rating_broadness: "BRIX",
    rating_citation: "CITX",
    rating_litigation: "RUBY",
    portfolio_score: "PVIX",
    family_id: "FAMILY ID",
    extended_family_id: "EXTENDED FAMILY ID",
    cpc_codes: "CPC CODES",
    uspc_codes: "USPS CODES",
    country: "COUNTRY",
    doc_category: "DOC CATEGORY",
    transaction_type: "TRANSACTION TYPE",
    publication_type: "PUBLICATION TYPE",
    rnix_score: "RNIX",
    publication_status: "PUBLICATION STATUS",
    opposition_country: "OPPOSITION COUNTRY",
    full_text: "FULL TEXT",
    full_text_fields: "FULL TEXT FIELDS",
    is_litigated: "IS LITIGATED",
    is_challenged: "IS CHALLENGED"
  }[qs];
};

export const keepOnlyReexamQueryStrings = query => {
  return omitBy(
    pick(query, [
      "application_number",
      "filing_date",
      "application_status",
      "status_date",
      "art_unit",
      "national_class",
      "national_subclass",
      "patent_number",
      "examiner",
      "law_firm",
      "owner",
      "requester",
      "exparte_status"
    ]),
    v => v === "" || v === null || v === undefined
  );
};

export const mapReexamQueryStringToName = qs => {
  return {
    application_number: "APPLICATION NUMBER",
    filing_date: "FILING DATE",
    application_status: "APPLICATION STATUS",
    status_date: "STATUS DATE",
    art_unit: "ART UNIT",
    national_class: "CLASS",
    national_subclass: "SUBCLASS",
    patent_number: "PATENT NUMBER",
    examiner: "EXAMINER",
    law_firm: "LAW FIRM",
    owner: "OWNER",
    requester: "REQUESTER",
    exparte_status: "STATUS"
  }[qs];
};

export const mapInternationalQueryStringToName = qs => {
  return {
    case_number: "Case Number",
    patent_number: "Patent Number",
    filing_date: "Filing Date",
    patent_owner: "Patent Owner",
    petitioner: "Petitioner",
    status: "Status",
    decision_date: "Decision Date",
    jurisdiction: "Jurisdiction"
  }[qs];
};

export const mapUPCQueryStringToName = qs => {
  return {
    case_number: "Case Number",
    patent_number: "Patent Number",
    filing_date: "Filing Date",
    proceeding_type: "Proceeding Type",
    action_application_type: "Action/Application Type",
    respondent: "Respondent",
    claimant: "Claimant",
    representatives: "Representatives",
    judges: "Judges",
    "documents.full_text": "Full Text"
  }[qs];
};
